/**
 * Permission utilities for role-based access control
 */

interface User {
  is_admin?: boolean;
  role?: {
    role_id: number;
    name: string;
    permissions: Record<string, boolean>;
  };
}

/**
 * Check if user has a specific permission
 */
export function hasPermission(user: User | null, permission: string): boolean {
  if (!user) return false;
  
  // Backward compatibility: is_admin grants all permissions
  if (user.is_admin) return true;
  
  // Check role-based permissions
  if (!user.role) return false;
  
  const rolePermissions = user.role.permissions || {};
  
  // Check specific permission or all_features wildcard
  return rolePermissions[permission] === true || rolePermissions['all_features'] === true;
}

/**
 * Check if user can manage other users
 */
export function canManageUsers(user: User | null): boolean {
  return hasPermission(user, 'user_management');
}

/**
 * Check if user has admin-level access (either is_admin or Admin role)
 */
export function isAdminUser(user: User | null): boolean {
  if (!user) return false;
  return user.is_admin || user.role?.name === 'Admin' || false;
}

/**
 * Get display-friendly role name
 */
export function getRoleName(user: User | null): string {
  if (!user) return 'No Role';
  
  // Backward compatibility
  if (user.is_admin && !user.role) return 'Admin';
  
  return user.role?.name || 'No Role';
}

